import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, Typography } from '@mui/material';
import { Stack } from '@mui/system';

import ModalNotification from '@/shared/components/dialogs/ModalNotification';
import WaveUIForm_V2 from '@/shared/components/forms/WaveUIForm_V2';
import FieldType from '@/shared/components/forms/WaveUIForm_V2/types/fieldType.enum';
import countries from '@/shared/configs/countries.json';
import ProfilesTemplateSchemas from '@/shared/types/shipping/profilesTemplateSchemas.type';
import ShippingProfile from '@/shared/types/shipping/shippingProfile.type';
import Zone from '@/shared/types/shipping/zones/zone.type';

import AssignStates from './components/AssignStates/AssignStates';
import useAddZoneDialog from './hooks/useAddZoneDialog';

type AddZoneDialogProps = {
  templateSchemas: ProfilesTemplateSchemas;
  shippingProfile: ShippingProfile;
  zone?: Zone;
  zoneKey?: keyof Zone;
  errors?: string[];
  onClose: () => void;
  proceed: (
    value: Omit<Zone, 'delivery_state'> & { delivery_state: string[] },
    zoneKey?: keyof Zone,
  ) => void;
};

const AddZoneDialog: FC<AddZoneDialogProps> = ({
  templateSchemas,
  shippingProfile,
  zone,
  zoneKey,
  errors,
  onClose,
  proceed,
}) => {
  const { t } = useTranslation(['shipping', 'common']);

  const { form, formErrors, assignStatesState, setForm, onValidate } =
    useAddZoneDialog({ countries, shippingProfile, zone, zoneKey });

  const shippingRateForm = (
    <WaveUIForm_V2
      fields={[
        {
          field_type: FieldType.Select,
          field_props: {
            required: true,
            name: 'shipping_rate_type',
            label: t('forms:shipping_rate_type'),
            placeholder: t('forms:shipping_rate_type'),
            value: form.shipping_rate_type,
            options: templateSchemas.rate_types.map((item) => {
              const label: Record<string, string> = {
                flat_rate: 'Flat Rate',
              };

              return {
                label: label[item.shipping_rate_type],
                value: item.shipping_rate_type,
              };
            }),
            error: !!formErrors?.shipping_rate_type,
            helperText: formErrors?.shipping_rate_type,
          },
        },
        {
          field_type: FieldType.Number,
          field_props: {
            required: true,
            name: 'shipping_rate',
            label: t('forms:shipping_rate'),
            placeholder: t('forms:shipping_rate'),
            value: form.shipping_rate,
            error: !!formErrors?.shipping_rate,
            helperText: formErrors?.shipping_rate,
          },
        },
      ]}
      getFieldsValues={(value) => {
        setForm((prev) => ({ ...prev, ...value }));
      }}
    />
  );

  const hasErrors = errors && errors.length > 0;

  return (
    <ModalNotification
      fullWidth
      size='md'
      open
      onClose={onClose}
      dialogProps={{
        title: t('shipping:addZone'),
        content: hasErrors ? (
          <Stack direction='column' gap={2}>
            {errors.map((error, index) => (
              <Alert key={index} severity='error'>
                {error}
              </Alert>
            ))}
          </Stack>
        ) : (
          <Stack py={2} spacing={2} useFlexGap>
            {zoneKey === 'shipping_rate' ? (
              shippingRateForm
            ) : (
              <>
                <WaveUIForm_V2
                  fields={[
                    {
                      field_type: FieldType.Select,
                      field_props: {
                        required: true,
                        name: 'delivery_country',
                        label: t('forms:delivery_country'),
                        placeholder: t('forms:delivery_country'),
                        value: form.delivery_country,
                        options: countries.map((country) => ({
                          label: country.country,
                          value: country.code,
                        })),
                        error: !!formErrors?.delivery_country,
                        helperText: formErrors?.delivery_country,
                      },
                    },
                  ]}
                  getFieldsValues={(value) => {
                    setForm((prev) => ({ ...prev, ...value }));
                  }}
                />

                {form.delivery_country && (
                  <Stack spacing={1} mb={2}>
                    <Typography variant='body1' color='text.secondary'>
                      {t('shipping:assignStates')}
                    </Typography>
                    <AssignStates
                      assignStatesState={assignStatesState}
                      validationError={formErrors.deliver_state}
                    />
                  </Stack>
                )}

                {assignStatesState.assigned.length === 1 && (
                  <Stack spacing={1} mb={2}>
                    <WaveUIForm_V2
                      fields={[
                        {
                          field_type: FieldType.Text,
                          field_props: {
                            name: 'delivery_city',
                            label: t('forms:delivery_city'),
                            placeholder: t('forms:delivery_city'),
                            value: form.delivery_city,
                          },
                        },
                      ]}
                      getFieldsValues={(value) => {
                        setForm((prev) => ({ ...prev, ...value }));
                      }}
                    />
                  </Stack>
                )}

                {shippingRateForm}
              </>
            )}
          </Stack>
        ),
        actions: hasErrors
          ? [
              {
                label: t('common:cancel'),
                variant: 'text',
                onClick: onClose,
              },
            ]
          : [
              {
                label: t('common:cancel'),
                variant: 'text',
                onClick: onClose,
              },
              {
                label:
                  zoneKey === 'shipping_rate'
                    ? t('shipping:updateRate')
                    : t('shipping:addZone'),
                variant: 'contained',
                onClick: () => {
                  const isValid = onValidate();

                  if (isValid) {
                    proceed(
                      {
                        ...form,
                        delivery_state: assignStatesState.assigned.map(
                          (item) => item.value,
                        ),
                      } as Omit<Zone, 'delivery_state'> & {
                        delivery_state: string[];
                      },
                      zoneKey,
                    );
                  }
                },
              },
            ],
      }}
    />
  );
};

export default AddZoneDialog;
